import { AxiosError, AxiosRequestConfig } from "axios";
import { useCallback, useEffect, useReducer, useState } from "react";
import { axiosInstance } from "../services/axios-config/axios-config";

interface State<T> {
  loading: boolean;
  data?: T;
  error?: AxiosError;
}

interface Config {
  options?: AxiosRequestConfig<any>;
  onError?: (error: AxiosError) => void;
  initialFetch?: boolean;
  fetchIf?: boolean;
}

type Action<T> =
  | { type: "loading" }
  | { type: "fetched"; payload: T }
  | { type: "error"; payload: AxiosError };

const defaultConfig: Config = {
  options: {},
  initialFetch: true,
};

export default function useData<T = unknown>(
  url: string,
  config: Config = defaultConfig
) {
  const [trigger, setTrigger] = useState(0);
  const { options, onError, initialFetch, fetchIf } = config;
  const initialState = {
    data: undefined,
    error: undefined,
    loading: false,
  };
  const fetchReducer = (state: State<T>, action: Action<T>): State<T> => {
    switch (action.type) {
      case "loading": {
        return { ...initialState, loading: true };
      }
      case "fetched": {
        return { ...initialState, data: action.payload };
      }
      case "error": {
        return { ...initialState, error: action.payload };
      }
      default: {
        return state;
      }
    }
  };
  const [state, dispatch] = useReducer(fetchReducer, initialState);
  let cancelRequest = false;
  const fetchFunction = async () => {
    if (initialFetch === false && trigger === 0) {
      return;
    }
    if (fetchIf === false) {
      return;
    }
    dispatch({ type: "loading" });
    try {
      const response = await axiosInstance.get(url, options);
      if (cancelRequest) return;
      dispatch({ type: "fetched", payload: response.data });
    } catch (error: any) {
      if (cancelRequest) return;
      dispatch({ type: "error", payload: error as AxiosError });
      if (onError) {
        onError(error);
      }
    }
  };
  useEffect(() => {
    fetchFunction();
    return () => {
      cancelRequest = true;
    };
  }, [url, trigger, options]);

  const refetch = useCallback(() => {
    setTrigger((t) => t + 1);
  }, []);

  return { ...state, refetch, fetchFunction };
}
